import axios from 'axios';
import { ShotData } from '@/features/shot-visualization/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Quality options matching ShotData type
const qualityOptions: Array<'poor' | 'fair' | 'good' | 'excellent'> = ['poor', 'fair', 'good', 'excellent'];
const timingOptions: Array<'poor' | 'fair' | 'good' | 'excellent'> = ['poor', 'fair', 'good', 'excellent'];
const footworkOptions: Array<'poor' | 'fair' | 'good' | 'excellent'> = ['poor', 'fair', 'good', 'excellent'];
const balanceOptions: Array<'poor' | 'fair' | 'good' | 'excellent'> = ['poor', 'fair', 'good', 'excellent'];
const followThroughOptions: Array<'incomplete' | 'partial' | 'complete'> = ['incomplete', 'partial', 'complete'];

// Generate sample shots for demo purposes
const generateSampleShots = (count: number): ShotData[] => {
  const shotTypes = ['drive', 'cut', 'pull', 'sweep', 'hook', 'flick'];

  return Array.from({ length: count }, (_, i) => ({
    sessionId: `sample-session-${Math.floor(i / 5)}`,
    playerId: 'sample-player-1',
    tenantId: 'sample-tenant',
    timestamp: new Date(Date.now() - i * 60000).toISOString(),
    shotType: shotTypes[Math.floor(Math.random() * shotTypes.length)],
    quality: qualityOptions[Math.floor(Math.random() * qualityOptions.length)],
    ballSpeed: 30 + Math.random() * 15,
    batSpeed: 25 + Math.random() * 20,
    exitVelocity: 35 + Math.random() * 25,
    launchAngle: 15 + Math.random() * 30,
    horizontalAngle: -15 + Math.random() * 30,
    distance: 40 + Math.random() * 60,
    height: 5 + Math.random() * 15,
    spinRate: 1000 + Math.random() * 2000,
    impactLocationX: -0.5 + Math.random(),
    impactLocationY: -0.5 + Math.random(),
    sweetSpotDistance: Math.random() * 0.1,
    powerTransfer: 0.5 + Math.random() * 0.5,
    timing: timingOptions[Math.floor(Math.random() * timingOptions.length)],
    footwork: footworkOptions[Math.floor(Math.random() * footworkOptions.length)],
    balance: balanceOptions[Math.floor(Math.random() * balanceOptions.length)],
    followThrough: followThroughOptions[Math.floor(Math.random() * followThroughOptions.length)],
  }));
};

// Create axios instance with default config
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Shot API endpoints
export const shotsApi = {
  // Get shots for a session
  getSessionShots: async (sessionId: string): Promise<ShotData[]> => {
    const response = await api.get(`/sessions/${sessionId}/shots`);
    return response.data;
  },

  // Get shots for a player
  getPlayerShots: async (playerId: string, params?: {
    startDate?: string;
    endDate?: string;
    shotType?: string;
    limit?: number;
  }): Promise<ShotData[]> => {
    const response = await api.get(`/players/${playerId}/shots`, { params });
    return response.data;
  },

  // Get single shot with detailed metrics
  getShotMetrics: async (shotId: string) => {
    const response = await api.get(`/shots/${shotId}/metrics`);
    return response.data;
  },

  // Analyze shot physics
  analyzeShot: async (shotData: Partial<ShotData>) => {
    const response = await api.post('/shots/analyze', shotData);
    return response.data;
  },

  // Upload bulk shots
  bulkUpload: async (sessionId: string, shots: Partial<ShotData>[]) => {
    const response = await api.post('/shots/bulk', {
      sessionId,
      shots,
    });
    return response.data;
  },

  // Compare multiple shots
  compareShots: async (shotIds: string[], metrics?: string[]) => {
    const response = await api.get('/shots/comparison', {
      params: {
        shotIds,
        metrics,
      },
    });
    return response.data;
  },

  // Get aggregated statistics
  getAggregateStats: async (params: {
    playerId?: string;
    sessionId?: string;
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month' | 'shotType';
  }) => {
    const response = await api.get('/shots/aggregate', { params });
    return response.data;
  },

  // Get recent shots (for live monitoring)
  getRecentShots: async (limit: number = 10): Promise<ShotData[]> => {
    try {
      const response = await api.get('/shots/recent', {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch recent shots, using sample data');
      // Return sample data for demo purposes
      return generateSampleShots(limit);
    }
  },
  
  // Get trend data
  getTrendData: async (params: {
    playerId?: string;
    sessionId?: string;
    groupBy?: 'day' | 'week' | 'month';
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      const response = await api.get('/shots/trends', { params });
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch trend data');
      return { trends: [] };
    }
  },

  // Get field distribution
  getFieldDistribution: async (params: {
    playerId?: string;
    sessionId?: string;
    shotType?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    try {
      const response = await api.get('/shots/field-distribution', { params });
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch field distribution');
      return { distribution: [] };
    }
  },

  // Export data
  exportData: async (params: {
    format: 'csv' | 'excel' | 'pdf';
    data: Record<string, unknown>;
    filename?: string;
  }) => {
    const response = await api.post('/export', params);
    return response.data;
  },
};

export default shotsApi;